/**
 * useButton hook.
 */
import ListContext from '@shared/components/utils/context/listContext';
import TranslationManager from '@shared/assets/locale/translationsManager';
import Props from '@shared/types/props/pressable';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { selectMagicRemoteEnabled } from '@shared/store/slices/magicRemote.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useContext, useState, useEffect, useMemo } from 'react';

/**
 * Manages the button behavior and appearance.
 * Handles the button focus, enabled state, list index, and list focused.
 * Returns the callbacks, props, and button text for the default button.
 */
export default function useButton({
  action,
  releaseAction,
  focus,
  index,
  text,
  tip,
}: Props & { tip?: string }) {
  //----------------------------------
  // Variables
  //----------------------------------

  // Selectors
  const currentFocus = useAppSelector(selectFocus);
  const enabled = useAppSelector(selectMagicRemoteEnabled);
  const { listIndex, listFocused } = useContext(ListContext);

  // States
  const [focused, setFocused] = useState(
    (listFocused && listIndex === index) || focus === currentFocus
  );

  //----------------------------------
  // Effect hooks
  //----------------------------------

  useEffect(() => {
    setFocused((listFocused && listIndex === index) || focus === currentFocus);
  }, [currentFocus, listIndex, listFocused]);

  //----------------------------------
  // Memoized values
  //----------------------------------

  return useMemo(() => {
    const buttonFocused = !enabled && focused;
    const buttonTip = tip ? TranslationManager.get(tip) : undefined;
    const buttonText = text ? TranslationManager.get(text) : undefined;
    return {
      callbacks: {
        onClick: action || releaseAction,
      },
      props: {
        displayBackground: focused,
        displayIcon: focused || buttonFocused,
        focused: buttonFocused,
      },
      buttonText,
      buttonTip,
    };
  }, [currentFocus, enabled, focused, text, tip]);
}
